import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AuthShell } from './AuthShell';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Field } from '../common/Field';
import { Input } from '../common/Input';
import { PasswordStrengthMeter } from '../common/PasswordStrengthMeter';
import { apiError } from '../../utils/apiError';
import { useAuthOptions } from '../../hooks/useAuthOptions';

export function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { registrationEnabled, isLoading: optionsLoading } = useAuthOptions();

  const mismatch = Boolean(confirmPassword) && password !== confirmPassword;

  if (!optionsLoading && !registrationEnabled) {
    return (
      <AuthShell subtitle="Registration is disabled">
        <div className="space-y-5">
          <Alert tone="info">
            This PulseCheck instance does not accept self-registration. Ask an administrator to create an
            account for you, or use a guest passcode for read-only access.
          </Alert>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            <Link to="/login" className="text-brand-600 dark:text-brand-400 hover:underline">Back to sign in</Link>
            {' · '}
            <Link to="/guest" className="text-brand-600 dark:text-brand-400 hover:underline">Access as guest</Link>
          </p>
        </div>
      </AuthShell>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await register(name, email, password);
      navigate('/login', { state: { message: 'Account created. Please sign in.' } });
    } catch (err) {
      setError(apiError(err, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell subtitle="Create your account">
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && <Alert tone="error">{error}</Alert>}

        <Field label="Name" required>
          {p => (
            <Input {...p} type="text" autoComplete="name" required value={name} onChange={e => setName(e.target.value)} />
          )}
        </Field>

        <Field label="Email" required>
          {p => (
            <Input {...p} type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} />
          )}
        </Field>

        <div>
          <Field label="Password" required>
            {p => (
              <Input
                {...p}
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            )}
          </Field>
          <PasswordStrengthMeter password={password} />
        </div>

        <Field
          label="Confirm Password"
          required
          error={mismatch ? 'Passwords do not match' : undefined}
        >
          {p => (
            <Input
              {...p}
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
            />
          )}
        </Field>

        <Button type="submit" loading={loading} className="w-full" size="lg">
          {loading ? 'Creating account…' : 'Create account'}
        </Button>

        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-600 dark:text-brand-400 hover:underline">Sign in</Link>
        </p>
      </form>
    </AuthShell>
  );
}
